import { LandingHeader } from "../components/landing/LandingHeader";
import { LandingFooter } from "../components/landing/LandingFooter";

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <LandingHeader />
      <main className="flex-1 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            Terms of Service
          </h1>

          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">Last updated: January 22, 2025</p>

            {/* Section 1: Agreement to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Agreement to Terms
              </h2>
              <p className="text-gray-600 mb-4">
                These Terms of Service ("Terms") constitute a legally binding
                agreement between you and Case Sensitive Paweł Pikuła
                ("RunLens", "we", "us", or "our"), a company registered in
                Poland, European Union.
              </p>
              <p className="text-gray-600 mb-4">
                By accessing or using RunLens, you agree to be bound by these
                Terms and our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                . If you disagree with any part of these Terms, you may not
                access or use the service.
              </p>
              <p className="text-gray-600 mb-4">
                You must be at least 16 years of age to use this service. By
                using RunLens, you represent and warrant that you meet this age
                requirement.
              </p>
            </section>

            {/* Section 2: Description of Service */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Description of Service
              </h2>
              <p className="text-gray-600 mb-4">
                RunLens is an AI-powered running analysis platform that helps
                users understand their training data, identify patterns, and
                improve performance through personalized insights. The service
                includes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Integration with third-party fitness platforms (Garmin
                  Connect, Strava)
                </li>
                <li>Activity data visualization and analysis</li>
                <li>AI-generated training insights and recommendations</li>
                <li>Interactive AI coaching chat</li>
              </ul>
              <p className="text-gray-600 mb-4">
                We reserve the right to modify, suspend, or discontinue any
                aspect of the service at any time, with or without notice.
              </p>
            </section>

            {/* Section 3: User Accounts */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. User Accounts
              </h2>
              <p className="text-gray-600 mb-4">
                To access certain features of RunLens, you must create an
                account. When creating and using your account, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Provide accurate, current, and complete registration
                  information
                </li>
                <li>
                  Maintain and promptly update your account information
                </li>
                <li>
                  Keep your password confidential and secure
                </li>
                <li>
                  Accept responsibility for all activities that occur under your
                  account
                </li>
                <li>
                  Notify us immediately of any unauthorized access or security
                  breach
                </li>
                <li>
                  Not share your account credentials with any third party
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                We reserve the right to suspend or terminate your account if we
                reasonably believe you have violated these Terms or engaged in
                fraudulent or illegal activity.
              </p>
            </section>

            {/* Section 4: Third-Party Integrations */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Third-Party Integrations
              </h2>
              <p className="text-gray-600 mb-4">
                RunLens integrates with third-party platforms including Garmin
                Connect and Strava. By connecting these services, you:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Authorize us to access and retrieve your activity data from
                  these platforms
                </li>
                <li>
                  Agree to comply with the terms of service of those platforms
                </li>
                <li>
                  Acknowledge that we are not responsible for the availability,
                  accuracy, or functionality of third-party services
                </li>
                <li>
                  Understand that your use of third-party services is governed
                  by their respective terms and privacy policies
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                We do not control and are not liable for any actions or content
                of third-party services. Your data from these integrations is
                handled in accordance with our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </section>

            {/* Section 5: Acceptable Use */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Acceptable Use
              </h2>
              <p className="text-gray-600 mb-4">
                You agree to use RunLens only for lawful purposes and in
                accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>
                  Attempt to gain unauthorized access to any part of the
                  service, other accounts, or computer systems
                </li>
                <li>
                  Interfere with or disrupt the service, servers, or networks
                </li>
                <li>
                  Use automated systems, bots, or scripts to access the service
                  without our express permission
                </li>
                <li>
                  Scrape, data mine, or extract data from the service for
                  unauthorized purposes
                </li>
                <li>
                  Reverse engineer, decompile, or attempt to extract the source
                  code of the service
                </li>
                <li>
                  Impersonate any person or entity, or falsely represent your
                  affiliation
                </li>
                <li>
                  Upload malicious code, viruses, or harmful content
                </li>
                <li>
                  Violate any applicable laws or regulations
                </li>
              </ul>
            </section>

            {/* Section 6: AI-Generated Content */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. AI-Generated Content and Health Disclaimer
              </h2>
              <p className="text-gray-600 mb-4">
                RunLens uses artificial intelligence to analyze your training
                data and generate insights, recommendations, and coaching
                responses. By using these features, you expressly acknowledge
                and agree that:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  <strong>Not Medical Advice:</strong> AI-generated content is
                  for informational and educational purposes only. It does not
                  constitute medical advice, diagnosis, or treatment.
                </li>
                <li>
                  <strong>Not Professional Coaching:</strong> AI insights do not
                  replace the guidance of certified coaches, trainers, or sports
                  professionals.
                </li>
                <li>
                  <strong>No Guarantees:</strong> We do not guarantee the
                  accuracy, completeness, reliability, or suitability of
                  AI-generated content for any particular purpose.
                </li>
                <li>
                  <strong>Results May Vary:</strong> Training outcomes depend on
                  many factors. Following AI recommendations does not guarantee
                  any specific results.
                </li>
                <li>
                  <strong>Assumption of Risk:</strong> You assume all risks
                  associated with acting on AI-generated insights or
                  recommendations.
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                <strong>
                  Always consult a qualified healthcare professional before
                  starting any new exercise program, making significant changes
                  to your training, or if you have any health concerns.
                </strong>
              </p>
            </section>

            {/* Section 7: Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Intellectual Property
              </h2>
              <p className="text-gray-600 mb-4">
                The service and its original content, features, functionality,
                and underlying technology are and shall remain the exclusive
                property of Case Sensitive Paweł Pikuła. This includes, but is
                not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>The RunLens name, logo, and branding</li>
                <li>Software, algorithms, and AI models</li>
                <li>User interface design and visual elements</li>
                <li>Documentation and instructional materials</li>
              </ul>
              <p className="text-gray-600 mb-4">
                The service is protected by copyright, trademark, and other
                intellectual property laws of Poland, the European Union, and
                other jurisdictions. You may not copy, modify, distribute, sell,
                or lease any part of the service without our express written
                consent.
              </p>
            </section>

            {/* Section 8: User Content and Data */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. User Content and Data
              </h2>
              <p className="text-gray-600 mb-4">
                You retain full ownership of your personal data and activity
                information. By using RunLens, you grant us a limited,
                non-exclusive, worldwide, royalty-free license to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Store, process, and analyze your data to provide the service
                </li>
                <li>
                  Generate AI-powered insights based on your activity data
                </li>
                <li>
                  Use aggregated, anonymized data to improve the service
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                This license terminates when you delete your account. For
                detailed information about how we handle your data, please refer
                to our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </section>

            {/* Section 9: Disclaimer of Warranties */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Disclaimer of Warranties
              </h2>
              <p className="text-gray-600 mb-4">
                THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
                WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT.
              </p>
              <p className="text-gray-600 mb-4">We do not warrant that:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  The service will be uninterrupted, timely, secure, or
                  error-free
                </li>
                <li>
                  The results obtained from using the service will be accurate
                  or reliable
                </li>
                <li>
                  AI-generated insights will be helpful, accurate, or suitable
                  for your needs
                </li>
                <li>
                  The service will meet your specific requirements or
                  expectations
                </li>
                <li>
                  Any errors in the service will be corrected
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                Some jurisdictions do not allow the exclusion of certain
                warranties. In such cases, the above exclusions may not apply to
                you to the extent prohibited by applicable law.
              </p>
            </section>

            {/* Section 10: Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Limitation of Liability
              </h2>
              <p className="text-gray-600 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, CASE
                SENSITIVE PAWEŁ PIKUŁA AND ITS OFFICERS, EMPLOYEES, AGENTS, AND
                AFFILIATES SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>
                  Any indirect, incidental, special, consequential, or punitive
                  damages
                </li>
                <li>
                  Loss of profits, data, use, goodwill, or other intangible
                  losses
                </li>
                <li>
                  Any injury, illness, or health consequences resulting from
                  following AI-generated training recommendations
                </li>
                <li>
                  Any damages arising from third-party integrations or services
                </li>
              </ul>
              <p className="text-gray-600 mb-4">
                In no event shall our total liability exceed the amount you paid
                to us, if any, in the twelve (12) months preceding the claim, or
                one hundred euros (EUR 100), whichever is greater.
              </p>
              <p className="text-gray-600 mb-4">
                Nothing in these Terms shall limit or exclude our liability for
                death or personal injury caused by our negligence, fraud, or any
                other liability that cannot be excluded under applicable law,
                including mandatory consumer protection rights under EU law.
              </p>
            </section>

            {/* Section 11: Indemnification */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Indemnification
              </h2>
              <p className="text-gray-600 mb-4">
                You agree to indemnify, defend, and hold harmless Case Sensitive
                Paweł Pikuła and its officers, directors, employees, and agents
                from and against any claims, liabilities, damages, losses, and
                expenses (including reasonable legal fees) arising out of or
                related to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Your use of the service</li>
                <li>Your violation of these Terms</li>
                <li>
                  Your violation of any rights of another person or entity
                </li>
                <li>
                  Any content you submit or transmit through the service
                </li>
              </ul>
            </section>

            {/* Section 12: Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Termination
              </h2>
              <p className="text-gray-600 mb-4">
                <strong>Termination by You:</strong> You may terminate your
                account at any time by using the account deletion feature in
                your settings or by contacting us at{" "}
                <a
                  href="mailto:privacy@runlens.ai"
                  className="text-blue-600 hover:underline"
                >
                  privacy@runlens.ai
                </a>
                .
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Termination by Us:</strong> We may terminate or suspend
                your account immediately, without prior notice or liability, for
                any reason, including if you breach these Terms.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Effect of Termination:</strong> Upon termination, your
                right to use the service will cease immediately. Your data will
                be handled in accordance with our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                , including the 30-day retention period for account recovery.
              </p>
            </section>

            {/* Section 13: Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Changes to Terms
              </h2>
              <p className="text-gray-600 mb-4">
                We reserve the right to modify these Terms at any time. When we
                make changes, we will update the "Last updated" date at the top
                of this page.
              </p>
              <p className="text-gray-600 mb-4">
                For material changes, we will provide notice through the service
                or by sending an email to the address associated with your
                account at least 30 days before the changes take effect.
              </p>
              <p className="text-gray-600 mb-4">
                Your continued use of RunLens after any changes to these Terms
                constitutes your acceptance of the revised Terms. If you do not
                agree to the new Terms, you must stop using the service.
              </p>
            </section>

            {/* Section 14: Dispute Resolution and Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                14. Dispute Resolution and Governing Law
              </h2>
              <p className="text-gray-600 mb-4">
                <strong>Informal Resolution:</strong> Before filing any formal
                legal claim, you agree to contact us at{" "}
                <a
                  href="mailto:privacy@runlens.ai"
                  className="text-blue-600 hover:underline"
                >
                  privacy@runlens.ai
                </a>{" "}
                and attempt to resolve the dispute informally for at least 30
                days.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Governing Law:</strong> These Terms shall be governed by
                and construed in accordance with the laws of Poland and
                applicable European Union regulations, without regard to
                conflict of law principles.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Jurisdiction:</strong> Any disputes arising from these
                Terms or your use of the service shall be subject to the
                exclusive jurisdiction of the courts of Poland.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>EU Consumer Rights:</strong> If you are a consumer
                residing in the European Union, you retain any mandatory
                consumer protection rights granted by the laws of your country
                of residence. Nothing in these Terms affects your statutory
                rights as a consumer.
              </p>
            </section>

            {/* Section 15: Severability */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                15. Severability
              </h2>
              <p className="text-gray-600 mb-4">
                If any provision of these Terms is found to be unenforceable or
                invalid by a court of competent jurisdiction, that provision
                shall be limited or eliminated to the minimum extent necessary,
                and the remaining provisions shall continue in full force and
                effect.
              </p>
            </section>

            {/* Section 16: Entire Agreement */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                16. Entire Agreement
              </h2>
              <p className="text-gray-600 mb-4">
                These Terms, together with our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                , constitute the entire agreement between you and Case Sensitive
                Paweł Pikuła regarding the use of RunLens and supersede any
                prior agreements or understandings.
              </p>
            </section>

            {/* Section 17: Contact Us */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                17. Contact Us
              </h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about these Terms of Service, please
                contact us:
              </p>
              <div className="text-gray-600 mb-4">
                <p>
                  <strong>Case Sensitive Paweł Pikuła</strong>
                </p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:privacy@runlens.ai"
                    className="text-blue-600 hover:underline"
                  >
                    privacy@runlens.ai
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
